<script setup lang="ts">
/**
 * Cookie Banner - Laumeris
 * Bandeau de consentement cookies conforme CNIL
 * 
 * Conformité :
 * - Refus aussi simple qu'acceptation
 * - Pas de dark patterns
 * - Choix granulaire disponible
 * - Informations claires et accessibles
 */

import { useCookiesStore } from '@/stores/cookies'

const cookiesStore = useCookiesStore()
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="translate-y-full opacity-0"
      enter-to-class="translate-y-0 opacity-100"
      leave-active-class="transition-all duration-200 ease-in"
      leave-from-class="translate-y-0 opacity-100"
      leave-to-class="translate-y-full opacity-0"
    >
      <div
        v-if="cookiesStore.showBanner && !cookiesStore.showSettings"
        class="fixed bottom-0 inset-x-0 z-[9999] p-4 sm:p-6"
      >
        <div 
          class="max-w-4xl mx-auto bg-white dark:bg-corporate-900 rounded-2xl shadow-2xl border border-corporate-200 dark:border-corporate-700 overflow-hidden"
        >
          <!-- Barre de progression subtile en haut -->
          <div class="h-1 bg-gradient-to-r from-brand-gold-400 via-brand-gold-500 to-brand-gold-400" />
          
          <div class="p-6">
            <div class="flex flex-col lg:flex-row lg:items-start gap-6">
              <!-- Icône et texte -->
              <div class="flex-1">
                <div class="flex items-start gap-4">
                  <!-- Icône cookie -->
                  <div class="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-[#1E3A5F] to-[#152238] flex items-center justify-center">
                    <svg class="w-6 h-6 text-[#C9B896]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  
                  <div class="flex-1 min-w-0">
                    <h2 class="text-lg font-semibold text-corporate-900 dark:text-white mb-2">
                      Nous respectons votre vie privée
                    </h2>
                    <p class="text-sm text-corporate-600 dark:text-corporate-400 leading-relaxed">
                      Nous utilisons des cookies pour assurer le bon fonctionnement du site et améliorer votre expérience. 
                      Vous pouvez accepter, refuser ou personnaliser vos choix à tout moment.
                      <router-link 
                        to="/legal/cookies" 
                        class="text-accent-600 hover:text-accent-700 dark:text-accent-400 dark:hover:text-accent-300 underline underline-offset-2"
                      >
                        En savoir plus
                      </router-link>
                    </p>
                  </div>
                </div>
              </div>
              
              <!-- Boutons d'action -->
              <div class="flex flex-col sm:flex-row gap-3 lg:flex-shrink-0">
                <!-- Refuser - Même prominence que Accepter (conformité CNIL) -->
                <button
                  @click="cookiesStore.rejectAll()"
                  class="px-5 py-2.5 text-sm font-medium rounded-lg border-2 border-corporate-300 dark:border-corporate-600 text-corporate-700 dark:text-corporate-300 hover:bg-corporate-100 dark:hover:bg-corporate-800 transition-colors"
                >
                  Tout refuser
                </button>
                
                <!-- Personnaliser -->
                <button
                  @click="cookiesStore.openSettings()"
                  class="px-5 py-2.5 text-sm font-medium rounded-lg border-2 border-corporate-300 dark:border-corporate-600 text-corporate-700 dark:text-corporate-300 hover:bg-corporate-100 dark:hover:bg-corporate-800 transition-colors"
                >
                  Personnaliser
                </button>
                
                <!-- Accepter -->
                <button
                  @click="cookiesStore.acceptAll()"
                  class="px-5 py-2.5 text-sm font-medium rounded-lg bg-gradient-to-r from-[#1E3A5F] to-[#152238] text-white hover:from-[#2a4a73] hover:to-[#1a2d47] transition-all shadow-lg shadow-corporate-900/20"
                >
                  Tout accepter
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

